import React, { useState, useEffect, useRef } from 'react';
import portfolioAPI from '../services/portfolioAPI';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [personalInfo, setPersonalInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareHover, setShareHover] = useState(false);
  const [dailyMantra, setDailyMantra] = useState('');
  const [copyStatus, setCopyStatus] = useState('');

  // Array of mantras from great scholars
  const mantras = [
    {
      text: "The only way to do great work is to love what you do.",
      author: "Steve Jobs"
    },
    {
      text: "Innovation distinguishes between a leader and a follower.",
      author: "Steve Jobs"
    },
    {
      text: "Life is what happens when you're busy making other plans.",
      author: "John Lennon"
    },
    {
      text: "The future belongs to those who believe in the beauty of their dreams.",
      author: "Eleanor Roosevelt"
    },
    {
      text: "It is during our darkest moments that we must focus to see the light.",
      author: "Aristotle"
    },
    {
      text: "The only impossible journey is the one you never begin.",
      author: "Tony Robbins"
    },
    {
      text: "Success is not final, failure is not fatal: it is the courage to continue that counts.",
      author: "Winston Churchill"
    },
    {
      text: "Believe you can and you're halfway there.",
      author: "Theodore Roosevelt"
    },
    {
      text: "The best time to plant a tree was 20 years ago. The second best time is now.",
      author: "Chinese Proverb"
    },
    {
      text: "Don't watch the clock; do what it does. Keep going.",
      author: "Sam Levenson"
    },
    {
      text: "Everything you want is on the other side of fear.",
      author: "George Addair"
    },
    {
      text: "Great minds discuss ideas; average minds discuss events; small minds discuss people.",
      author: "Eleanor Roosevelt"
    },
    {
      text: "The purpose of our lives is to be happy.",
      author: "Dalai Lama"
    },
    {
      text: "Get busy living or get busy dying.",
      author: "Stephen King"
    },
    {
      text: "You miss 100% of the shots you don't take.",
      author: "Wayne Gretzky"
    }
  ];

  useEffect(() => {
    const fetchPersonalInfo = async () => {
      try {
        setLoading(true);
        const info = await portfolioAPI.getPersonalInfo().catch(err => {
          console.log('Personal info API failed, using fallback');
          return {
            name: "Sai Kowshik Suggala",
            email: "saikowshiksuggala9390@gmail.com",
            phone: "+91 9390106725",
            location: "India",
            linkedin: "https://www.linkedin.com/in/kowshiksuggala25",
            github: "https://github.com/KowshikSuggala25",
            instagram: "https://www.instagram.com/skmb_25/?hl=en"
          };
        });
        setPersonalInfo(info);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching personal info:', error);
        setLoading(false);
      }
    };

    fetchPersonalInfo();

    // Set daily mantra based on current date
    const today = new Date().getDate();
    setDailyMantra(mantras[today % mantras.length]);
  }, []);

  // Close the share FAB when clicking/tapping outside it (works for mobile)
  const shareFabRef = useRef(null);
  useEffect(() => {
    const handleDocPointer = (e) => {
      if (!shareFabRef.current) return;
      if (!shareFabRef.current.contains(e.target)) {
        setShareOpen(false);
      }
    };

    document.addEventListener('click', handleDocPointer);
    document.addEventListener('touchstart', handleDocPointer);

    return () => {
      document.removeEventListener('click', handleDocPointer);
      document.removeEventListener('touchstart', handleDocPointer);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');
    
    try {
      await portfolioAPI.submitContactForm(formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      console.log('Contact form submitted successfully');
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      
      setTimeout(() => {
        setSubmitStatus('');
      }, 5000);
    }
  };

  const handleCopyPageLink = async () => {
    try {
      const pageUrl = window.location.href;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(pageUrl);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = pageUrl;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      setCopyStatus('copied');
    } catch (error) {
      console.error('Failed to copy page link:', error);
      setCopyStatus('failed');
    } finally {
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  const getWhatsAppChatUrl = () => {
    const rawPhone = personalInfo?.phone || '9390106725';
    const digitsOnly = String(rawPhone).replace(/\D/g, '');
    const normalizedPhone = digitsOnly.length === 10 ? `91${digitsOnly}` : digitsOnly;
    const message = encodeURIComponent('Hi, I need chat assistance.');
    return `https://wa.me/${normalizedPhone}?text=${message}`;
  };

  if (loading) {
    return (
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Get In Touch</h2>
            <div className="title-underline"></div>
          </div>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading contact information...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="contact-section">
      {/* Share floating button (bottom-left) on contact page */}
      <div
        ref={shareFabRef}
        className={`share-fab ${shareOpen ? 'open' : ''} ${shareHover ? 'hover' : ''}`}
        onMouseEnter={() => setShareHover(true)}
        onMouseLeave={() => setShareHover(false)}
      >
        <button
          className="share-button"
          aria-label="Share"
          onClick={() => setShareOpen((s) => !s)}
          title="Share"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="share-icon">
            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7a3.3 3.3 0 000-1.4l7.05-4.11A2.99 2.99 0 0018 7.91 3 3 0 1015 4a3 3 0 002.96 2.09l-7.05 4.11A3 3 0 109 15a3 3 0 001.91-.71l7.12 4.13c-.01.06-.03.12-.03.18A3 3 0 1018 16.08z" />
          </svg>
        </button>

        <div className="share-tree" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="share-item link"
            onClick={handleCopyPageLink}
            title={copyStatus === 'copied' ? 'Link copied!' : copyStatus === 'failed' ? 'Copy failed' : 'Copy page link'}
            aria-label="Copy page link"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.9 12a5.1 5.1 0 015.1-5.1h4v2h-4a3.1 3.1 0 000 6.2h4v2h-4A5.1 5.1 0 013.9 12zm7.1 1h2v-2h-2v2zm4-6h-4v2h4a3.1 3.1 0 010 6.2h-4v2h4a5.1 5.1 0 000-10.2z" />
            </svg>
          </button>

          <a
            className="share-item whatsapp"
            href={getWhatsAppChatUrl()}
            target="_blank"
            rel="noopener noreferrer"
            title="WhatsApp chat assistance"
            aria-label="Open WhatsApp chat assistance"
          >
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.52 3.48A11.9 11.9 0 0012.07 0C5.53 0 .22 5.31.22 11.85c0 2.09.55 4.14 1.59 5.94L0 24l6.41-1.68a11.83 11.83 0 005.66 1.44h.01c6.54 0 11.85-5.31 11.85-11.85 0-3.17-1.23-6.14-3.41-8.43zM12.08 21.7h-.01a9.8 9.8 0 01-4.99-1.36l-.36-.21-3.8 1 1.01-3.7-.24-.38a9.79 9.79 0 01-1.5-5.2C2.2 6.44 6.67 1.97 12.08 1.97c2.62 0 5.08 1.02 6.93 2.88a9.73 9.73 0 012.86 6.96c0 5.41-4.39 9.89-9.79 9.89zm5.37-7.36c-.29-.14-1.72-.85-1.99-.95-.27-.1-.46-.14-.65.14-.19.29-.75.95-.92 1.15-.17.19-.33.22-.62.07-.29-.14-1.21-.45-2.3-1.42-.85-.76-1.42-1.69-1.58-1.97-.17-.29-.02-.44.12-.59.13-.12.29-.33.43-.5.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.65-1.57-.89-2.15-.23-.55-.47-.47-.65-.48h-.55c-.19 0-.5.07-.76.36-.26.29-1 1-1 2.43s1.02 2.81 1.16 3.01c.14.19 2 3.06 4.85 4.29.68.29 1.2.46 1.61.59.68.22 1.3.19 1.79.12.55-.08 1.72-.7 1.97-1.37.24-.67.24-1.25.17-1.37-.07-.12-.26-.19-.55-.33z"/></svg>
          </a>

          <a
            className="share-item instagram"
            href={personalInfo?.instagram || 'https://www.instagram.com/skmb_25/'}
            target="_blank"
            rel="noopener noreferrer"
            title="Instagram"
          >
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98C.014 8.33 0 8.738 0 11.998c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947C22.782 2.618 20.361.192 15.999 0 14.718-.058 14.31 0 12 0z"/></svg>
          </a>

          <a
            className="share-item github"
            href={personalInfo?.github || 'https://github.com/KowshikSuggala25'}
            target="_blank"
            rel="noopener noreferrer"
            title="GitHub"
          >
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.372 0 0 5.372 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386C24 5.372 18.627 0 12 0z"/></svg>
          </a>

          <a
            className="share-item linkedin"
            href={personalInfo?.linkedin || 'https://www.linkedin.com/in/kowshiksuggala25'}
            target="_blank"
            rel="noopener noreferrer"
            title="LinkedIn"
          >
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          </a>
        </div>
      </div>
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Get In Touch</h2>
          <div className="title-underline"></div>
        </div>
        <div className="contact-content">
          <div className="contact-info">
            <h3>Let's Connect</h3>
            <p>
              I'm always open to discussing new opportunities, interesting projects, 
              or just having a friendly conversation about technology.
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon">📧</div>
                <div className="contact-text">
                  <h4>Email</h4>
                  <p>{personalInfo?.email || "saikowshiksuggala9390@gmail.com"}</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📱</div>
                <div className="contact-text">
                  <h4>Phone</h4>
                  <p>{"+91 9390106725"}</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div className="contact-text">
                  <h4>Location</h4>
                  <p>{personalInfo?.location || "India"}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="contact-form-container">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your Name"
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Your Email"
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="Subject"
                  required
                />
              </div>
              <div className="form-group">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Your Message"
                  rows="5"
                  required
                ></textarea>
              </div>
              <button 
                type="submit" 
                className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              {submitStatus === 'success' && (
                <div className="success-message">
                  Message sent successfully! I'll get back to you soon.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="error-message">
                  Failed to send message. Please try again or contact me directly.
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Daily Mantra Section */}
        {dailyMantra && (
          <div className="daily-mantra-section">
            <div className="mantra-container">
              <div className="mantra-quote-icon">"</div>
              <p className="mantra-text">{dailyMantra.text}</p>
              <p className="mantra-author">— {dailyMantra.author}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Contact;